import { Injectable, EventEmitter } from "@angular/core";
import { Observable, from } from "rxjs";
import {
	AuthenticationDetails,
	CognitoUserAttribute,
	IAuthenticationDetailsData
} from "amazon-cognito-identity-js";
import { MatDialog } from "@angular/material";
import { CookieService } from "ngx-cookie-service";
import { HttpClient, HttpBackend } from "@angular/common/http";
import * as moment from "moment";

import { userPool } from "./aws-sdk-helper";
import { TranslatePipe } from "../translate";
import { RegistrationService } from "../../login/registration-service/registration.service";
import { ForcePasswordChangeComponent } from "../../login/force-password-change/force-password-change.component";
import {
	ConfirmationFormValue,
	LoginFormValue,
	RegistraionFormValue,
	ResetPasswordFormValue,
	LoginResult,
	CaptchaRequiredResponse,
	LogoutState,
} from "../../models";
import { LoggedInUser } from "./user";
import { CognitoUser, isCognitoUser } from "./cognito-user";
import { createUserData } from "./aws-sdk-helper";
import { environment } from "environments/environment";
import { StoredUser, isStoredCognitoUser } from "./models";
import { SessionIdleService } from "../session-idle/session-idle.service";

const CAPTCHA_REQUIRED_COOKIE_NAME = "captcha-required";

interface AuthenticationDetailsDataWithValidationData extends IAuthenticationDetailsData {
	ValidationData?: {
		[key: string]: string;
	};
}

export interface AuthenticationState {
	state: "UNAUTHENTICATED" | "AUTHENTICATED" | "REFRESHING";
}

// Set the providedIn scope to root to make this a singleton service
@Injectable({
	providedIn: "root"
})
export class AuthenticationService {

	private http: HttpClient;
	private currentUser: LoggedInUser | undefined;
	private refreshObservable: Observable<LoggedInUser> | undefined = undefined;
	public authState: EventEmitter<AuthenticationState> = new EventEmitter();

	constructor(
		public dialog: MatDialog,
		private translate: TranslatePipe,
		private registrationService: RegistrationService,
		private cookieService: CookieService,
		private sessionIdleService: SessionIdleService,
		handler: HttpBackend,
	) {
		/*
		 * When injected, HttpBackend dispatches requests directly to the backend,
		 * without going through the interceptor chain
		*/
		this.http = new HttpClient(handler);
	}

	/**
	 * @return the current user that is logged in, or null if no such user exists
	 */
	public getCurrentUser(): LoggedInUser | undefined {
		if (this.currentUser) {
			return this.currentUser;
		} else {
			const storedData = localStorage.getItem("currentUser");
			if (storedData) {
				const storedUser = <StoredUser>JSON.parse(storedData);
				if (isStoredCognitoUser(storedUser)) {
					this.currentUser = new CognitoUser(storedUser);
				}
				return this.currentUser;
			} else {
				return undefined;
			}
		}
	}

	public saveCurrentUser(user: LoggedInUser) {
		this.currentUser = user;
		localStorage.setItem("currentUser", user.toJSONString());
	}

	public isLoggedIn() {
		return this.getCurrentUser() !== undefined;
	}

	public confirmRegistration(userForm: ConfirmationFormValue) {
		return new Promise<void>((resolve, reject) => {
			const cognitoUser = createUserData(userForm.email);
			cognitoUser.confirmRegistration(userForm.code.trim(), true, (err, result) => {
				if (err) {
					reject(err);
				} else {
					resolve();
				}
			});
		});
	}

	public logout(state?: LogoutState) {
		const user = this.getCurrentUser();
		if (user) {
			user.signOut();
		}
		this.currentUser = undefined;
		localStorage.removeItem("currentUser");
		this.authState.emit({ state: "UNAUTHENTICATED" });
		this.sessionIdleService.stopWatching();
		this.registrationService.navigateToLogin(state);
	}

	public login(userForm: LoginFormValue) {
		const loginPromise = new Promise<LoginResult>((resolve, reject) => {
			const authenticationDetailsData: AuthenticationDetailsDataWithValidationData = {
				Username: userForm.email,
				Password: userForm.password
			};
			const accountName = getAccountNameFromLocation();
			if (!accountName) {
				reject(new Error("failed to parse account name from URL"));
				return;
			}
			authenticationDetailsData.ValidationData = {
				"account-name": accountName
			};
			if (userForm.recaptchaCode) {
				authenticationDetailsData.ValidationData["recaptcha"] = userForm.recaptchaCode;
			}
			const authenticationDetails = new AuthenticationDetails(authenticationDetailsData);
			const cognitoUser = createUserData(userForm.email);
			// remove any cached or current user
			cognitoUser.authenticateUser(authenticationDetails, {
				totpRequired: async (challengeName, challengeParams) => {
					if (challengeName !== "SOFTWARE_TOKEN_MFA") {
						reject(new Error("unsupported mfa type"));
					} else {
						// TODO check that challengeParams.FRIENDLY_DEVICE_NAME is the device associated with this user
						//
						if (!!userForm.totpCode) {
							cognitoUser.sendMFACode(userForm.totpCode, {
								onSuccess: (session) => {
									this.deleteCaptchaRequiredCookie();
									const user = new CognitoUser(cognitoUser, session.getIdToken(), session.getAccessToken(), session.getRefreshToken());
									this.saveCurrentUser(user);
									resolve({
										result: true,
										captchaRequired: false,
										forcedPasswordChange: false
									});
								},
								onFailure: (err) => {
									reject(err);
								}
							}, "SOFTWARE_TOKEN_MFA"); // This string must match what is expected by cognito's api
						} else {
								resolve({
									result: false,
									captchaRequired: false,
									totpRequired: true,
									forcedPasswordChange: false
								});
						}
					}
				}, onSuccess: async (result) => {
					try {
						// successful login, no longer need to show a captcha
						this.deleteCaptchaRequiredCookie();
						const user = new CognitoUser(cognitoUser, result.getIdToken(), result.getAccessToken(), result.getRefreshToken());
						this.saveCurrentUser(user);
						resolve({
							result: true,
							captchaRequired: false,
							forcedPasswordChange: false
						});
					} catch (e) {
						reject(e);
					}
				},
				onFailure: async (cognitoError) => {
					if (
						cognitoError.code === "NotAuthorizedException" ||
						cognitoError.code === "UserNotFoundException" ||
						cognitoError.code === "UserLambdaValidationException"
					) {
						if (cognitoError.code === "UserLambdaValidationException" && cognitoError.message.indexOf("UserDisabled") >= 0) {
							reject(cognitoError);
						}
						// this is the "incorrect credentials" case
						// if the reason was a bad password, make sure we know whether to show a captcha
						const encodedEmail = encodeURIComponent(userForm.email);
						this.http
							.get(`${environment.API_HOST}/main/login/captcha?email=${encodedEmail}`)
							.subscribe((result: object) => {
								const captchaResponse = <CaptchaRequiredResponse>result;

								// either set or clear a cookie based on the response
								if (captchaResponse.captchaRequired) {
									const expireTime = moment.utc(captchaResponse.expireTime).local();
									this.cookieService.set(CAPTCHA_REQUIRED_COOKIE_NAME, "true", expireTime.toDate());
								} else {
									this.deleteCaptchaRequiredCookie();
								}

								resolve({
									result: false,
									captchaRequired: captchaResponse.captchaRequired
								});
							}, (captchaError) => {
								// reject with the original failure
								// any error messaging to the end-user should show the original cognito-generated error, not the captcha failing
								reject(cognitoError);
							});

					} else {
						// some other error
						reject(cognitoError);
					}
				},
				newPasswordRequired: (userAttributes, requiredAttributes) => {
					delete userAttributes.email_verified; // it's returned but not valid to submit
					const launchPasswordChangeDialog = (errMessage?: string) => {
						const dialogRef = this.dialog.open<ForcePasswordChangeComponent>(ForcePasswordChangeComponent);
						if (errMessage) {
							dialogRef.componentInstance.errMessage = errMessage;
						}
						dialogRef.afterClosed().subscribe((newPassword) => {
							cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
								onSuccess: async (session) => {
									// Wait for login then change the forcedPasswordChange value to true
									const result = await this.login({
										email: userForm.email,
										password: newPassword,
									});
									resolve({
										result: result.result,
										captchaRequired: result.captchaRequired,
										forcedPasswordChange: true
									});
								},
								onFailure: (err) => {
									if (err.code === "InvalidPasswordException") {
										// password didn't match the rules for valid passwords, make them try again
										launchPasswordChangeDialog(this.translate.transform("login.resetPasswordFailed", []));
										resolve({
											result: false,
											captchaRequired: false
										});
									} else {
										reject(err);
										this.registrationService.navigateToLogin();
									}
								}
							});
						});
					};
					launchPasswordChangeDialog();
				}
			});
		});
		loginPromise.then((result) => {
			if (result.result) {
				this.sessionIdleService.startWatching();
				this.authState.emit({ state: "AUTHENTICATED" });
			}
		}).catch((e) => {
			// No action to take here, if errors occurred logging in we don't need to start or stop the idle service
		});
		return loginPromise;
	}

	public refreshUser(user: LoggedInUser) {
		if (!this.refreshObservable) {
			this.refreshObservable = from(new Promise<LoggedInUser>(async (resolve, reject) => {
				try {
					this.authState.emit({ state: "REFRESHING" });
					const refreshedUser = await user.refreshSession();
					this.saveCurrentUser(refreshedUser);
					resolve(refreshedUser);
				} catch (err) {
					reject(err);
				}
			}));
			this.refreshObservable.subscribe(() => {
				this.refreshObservable = undefined;
				this.authState.emit({ state: "AUTHENTICATED" });
			});
		}
		return this.refreshObservable;
	}

	public register(userForm: RegistraionFormValue) {
		const attributeList: CognitoUserAttribute[] = [];

		attributeList.push(new CognitoUserAttribute({ Name: "email", Value: userForm.email }));
		attributeList.push(new CognitoUserAttribute({ Name: "custom:accountName", Value: userForm.account }));
		attributeList.push(new CognitoUserAttribute({ Name: "given_name", Value: userForm.firstname }));
		attributeList.push(new CognitoUserAttribute({ Name: "family_name", Value: userForm.lastname }));
		return new Observable<void>((observer) => {
			// validationData argument cast to any because the cognito types don't allow null
			userPool.signUp(userForm.email, userForm.password, attributeList, <any>undefined, (err, result) => {
				if (err) {
					if ((<any>err).code === "UserLambdaValidationException") {
						// one of two cases:
						// account name already exists, in which case it will look like
						// "PreSignUp failed with error Account with name <ACCOUNT_NAME> already exists."
						// account is being deleted, in which case it will look like
						// "PreSignUp failed with error Account with name <ACCOUNT_NAME> is currently being deleted."
						if (err.message.endsWith("is currently being deleted.")) {
							observer.error("error.message.registration.accountBeingDeleted");
						} else {
							observer.error("error.message.registration.accountAlreadyExists");
						}
					} else if ((<any>err).code === "UsernameExistsException") {
						observer.error("error.message.registration.emailAlreadyRegistered");
					} else {
						observer.error(err.message);
					}
				} else if (result) {
					observer.next();
				} else {
					observer.error("No result returned");
				}
				observer.complete();
			});
		});
	}

	public async resendRegistrationConfirmation(email: string) {
		return new Promise<void>((resolve, reject) => {
			const user = createUserData(email);
			if (user) {
				user.resendConfirmationCode((err, result) => {
					if (err) {
						reject(err);
					} else {
						resolve();
					}
				});
			} else {
				reject();
			}
		});
	}

	public async resetPassword(resetForm: ResetPasswordFormValue) {
		const user = this.getCurrentUser();
		if (user && isCognitoUser(user)) {
			await user.resetPassword(resetForm.currentPassword, resetForm.password);
		}
	}

	public forgotPassword(email: string) {
		const cognitoUser = createUserData(email);

		return new Promise<void>((resolve, reject) => {
			cognitoUser.forgotPassword({
				onSuccess: () => {
					resolve();
				},
				onFailure: (err: Error) => {
					if (err.message.indexOf("Attempt limit exceeded") >= 0) {
						reject(new Error("forgotPassword.attemptLimitExceeded"));
					} else {
						reject(new Error("forgotPassword.genericError"));
					}
				},
				inputVerificationCode() {
					resolve();
				}
			});
		});
	}

	/**
	 * True if the captcha cookie is set.
	 */
	public get isCaptchaRequiredFromCookie() {
		return this.cookieService.get(CAPTCHA_REQUIRED_COOKIE_NAME) === "true";
	}

	private deleteCaptchaRequiredCookie() {
		this.cookieService.delete(CAPTCHA_REQUIRED_COOKIE_NAME, "/");
		this.cookieService.delete(CAPTCHA_REQUIRED_COOKIE_NAME, "/login");
	}
}

function getAccountNameFromLocation() {
	// window.locaiton.href should be something like:
	// https://jprice.sdp-dev.cloud.impulse.com/wizard/home
	const m = /^([a-zA-Z0-9]+:\/\/)?([^.]+)/.exec(window.location.href);
	if (!!m) {
		return m[2];
	} else {
		return undefined;
	}
}
